// Athletes & Staff (/partner/team): a partner account's athletes and the staff who manage them.
// It replaced the partner's My athletes (/admin/users) and Team pages (partner nav redesign,
// 2026-11-05, screen 2c). Owners and team members both open it; only the owner gets "+ Invite"
// and the Managed-by select (the server enforces both: PATCH .../manager and /partner/team are
// owner-only).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';
import { AddAthleteModal } from './PartnerUsersPage';
import { StaffInvite } from './PartnerTeamPage';
import { humanField } from '../lib/conciergeActivityModel';
import './AthletesStaffPage.css';

const DAY = 86400000;

const toTime = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

// "Today", "5d ago", "3w ago", "4mo ago", "2y ago". Callers handle a missing stamp.
export function shortAgo(iso, now = Date.now()) {
  const t = toTime(iso);
  if (t === null) return '';
  const days = Math.floor((now - t) / DAY);
  if (days < 1) return 'Today';
  if (days < 14) return `${days}d ago`;
  if (days < 60) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

// Staff last login: "Never", "Today", "Yesterday", else "Oct 30, 2026".
function staffLogin(iso, now = Date.now()) {
  const t = toTime(iso);
  if (t === null) return 'Never';
  const d = new Date(t);
  const today = new Date(now);
  const dayNo = (x) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate()) / DAY;
  const diff = dayNo(today) - dayNo(d);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const initials = (name) => (name || '').split(/\s+/).filter(Boolean).slice(0, 2)
  .map((w) => w[0].toUpperCase()).join('') || '?';
const firstName = (name) => (name || '').split(/\s+/)[0] || '';
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const athleteMeta = (a) => [a.position_primary, a.graduation_year ? `Class of ${a.graduation_year}` : null, a.school]
  .filter(Boolean).join(' · ');

const editSub = (a) => {
  const field = a.last_profile_change_field ? humanField(a.last_profile_change_field) : '';
  const by = a.last_profile_change_by ? `by ${a.last_profile_change_by}` : '';
  return [field, by].filter(Boolean).join(' · ');
};

export default function AthletesStaffPage() {
  const { API_URL, user } = useAuth();
  const isOwner = user?.role === 'partner' && !user?.is_partner_member;
  const [roster, setRoster] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [filterId, setFilterId] = useState(null);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(() => {
    axios.get(`${API_URL}/partner/roster`)
      .then((r) => {
        setRoster(r.data); setError('');
        // A filtered person who is no longer on the staff (removed in the Invite modal) drops the filter.
        const staffIds = new Set((r.data.staff || []).map((s) => s.id));
        setFilterId((cur) => (cur === null || staffIds.has(cur) ? cur : null));
      })
      .catch(() => setError('Could not load your athletes and staff.'));
  }, [API_URL]);
  useEffect(() => { load(); }, [load]);

  // The Partner home's "+ Add athlete" links here with ?add=1 (and old /admin/users?add=1 links
  // are redirected here with it).
  const wantsAdd = searchParams.get('add') === '1';
  useEffect(() => { if (wantsAdd) setAdding(true); }, [wantsAdd]);

  const athletes = useMemo(() => roster?.athletes || [], [roster]);
  const staff = roster?.staff || [];
  // Counts come from the rows, so a Managed-by change moves an athlete between people at once.
  const counts = useMemo(() => athletes.reduce((m, a) => {
    const id = a.managed_by?.id;
    m[id] = (m[id] || 0) + 1;
    return m;
  }, {}), [athletes]);
  const filterPerson = staff.find((s) => s.id === filterId) || null;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return athletes.filter((a) => (filterId === null || a.managed_by?.id === filterId)
      && (!q || `${a.name} ${a.school || ''}`.toLowerCase().includes(q)));
  }, [athletes, filterId, search]);

  const changeManager = async (athlete, value) => {
    setError('');
    setStatus('');
    try {
      const r = await axios.patch(`${API_URL}/partner/athletes/${athlete.user_id}/manager`,
        { managed_by_user_id: Number(value) });
      setRoster((cur) => ({
        ...cur,
        athletes: cur.athletes.map((a) => (a.user_id === athlete.user_id ? r.data : a)),
      }));
    } catch (err) {
      setError(`Could not change who manages ${athlete.name}. Try again.`);
    }
  };

  // Closing the Add modal also drops ?add=1, so a reload does not reopen it.
  const closeAdd = () => {
    setAdding(false);
    if (searchParams.has('add')) {
      const next = new URLSearchParams(searchParams);
      next.delete('add');
      setSearchParams(next, { replace: true });
    }
  };
  const created = (msg) => { setStatus(msg); closeAdd(); load(); };
  const closeInvite = () => { setInviting(false); load(); };

  const subtitle = roster
    ? `${roster.owner_name}'s partner account · ${plural(athletes.length, 'athlete', 'athletes')} · ${staff.length} staff`
    : null;

  return (
    <AdminPage
      active="team"
      eyebrow="MY TEAM"
      title="Athletes & Staff"
      subtitle={subtitle}
      actions={(
        <button type="button" className="adm-btn adm-btn--primary" onClick={() => setAdding(true)}
          data-testid="athletes_staff_add">
          + Add athlete
        </button>
      )}
    >
      {status && <div role="status" className="settings-toast">{status}</div>}
      {error && <div role="alert" className="settings-toast settings-toast--error">{error}</div>}

      <div className="as-grid">
        <section className="adm__card adm__card--flush as-athletes" aria-labelledby="as-athletes-title">
          <div className="as-athletes__head">
            <h2 className="as-athletes__title" id="as-athletes-title">Athletes</h2>
            {filterPerson && (
              <button type="button" className="adm-chip adm-chip--dark as-filter-chip"
                onClick={() => setFilterId(null)} data-testid="athletes_staff_filter_chip">
                {filterPerson.name} ✕
              </button>
            )}
            <input className="adm-input as-search" type="search" placeholder="Search athletes"
              aria-label="Search athletes" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {!roster ? (
            !error && <div className="adm-loading">Loading…</div>
          ) : athletes.length === 0 ? (
            <p className="as-empty">No athletes yet. Add one with “+ Add athlete”.</p>
          ) : visible.length === 0 ? (
            <p className="as-empty">No athletes match.</p>
          ) : (
            <div className="adm-tablewrap">
              <table className="adm-table as-table">
                <thead>
                  <tr>
                    <th>Athlete</th>
                    <th>Last login</th>
                    <th>Last profile edit</th>
                    <th>Managed by</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((a) => (
                    <tr key={a.user_id} data-testid="athletes_staff_row">
                      <td>
                        <div className="as-athlete">
                          <span className="as-avatar" aria-hidden="true">{initials(a.name)}</span>
                          <div className="as-athlete__text">
                            <Link className="adm-cell__name as-athlete__name" to={`/athletes/${a.user_id}`}
                              data-testid="athletes_staff_name">{a.name}</Link>
                            <div className="adm-cell__meta">{athleteMeta(a)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="as-nowrap">
                        {a.last_login_at
                          ? <span className="as-strong">{shortAgo(a.last_login_at)}</span>
                          : <span className="adm-never">Never</span>}
                      </td>
                      <td className="as-nowrap">
                        {a.last_profile_change_at ? (
                          <>
                            <div className="as-strong">{shortAgo(a.last_profile_change_at)}</div>
                            {editSub(a) && <div className="adm-cell__meta">{editSub(a)}</div>}
                          </>
                        ) : <span className="adm-cell__meta">—</span>}
                      </td>
                      <td>
                        {isOwner ? (
                          <select className="adm-select as-manager" value={String(a.managed_by?.id ?? '')}
                            aria-label={`Managed by for ${a.name}`}
                            onChange={(e) => changeManager(a, e.target.value)}>
                            {staff.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                          </select>
                        ) : (
                          <span className="adm-chip">{firstName(a.managed_by?.name)}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="adm__card as-staff" aria-labelledby="as-staff-title">
          <div className="as-staff__head">
            <h2 className="adm-subhead as-staff__title" id="as-staff-title">Your staff</h2>
            {isOwner && (
              <button type="button" className="as-invite" onClick={() => setInviting(true)}
                data-testid="athletes_staff_invite">
                + Invite
              </button>
            )}
          </div>
          <ul className="as-staff__list" aria-label="Your staff">
            <li>
              <button type="button" className={`as-person${filterId === null ? ' as-person--on' : ''}`}
                aria-pressed={filterId === null} onClick={() => setFilterId(null)}>
                <span className="as-avatar as-avatar--count">{athletes.length}</span>
                <span className="as-person__name">Everyone</span>
              </button>
            </li>
            {staff.map((s) => (
              <li key={s.id}>
                <button type="button" className={`as-person${filterId === s.id ? ' as-person--on' : ''}`}
                  aria-pressed={filterId === s.id} onClick={() => setFilterId(s.id)}
                  title={plural(counts[s.id] || 0, 'athlete', 'athletes')}>
                  <span className="as-avatar" aria-hidden="true">{initials(s.name)}</span>
                  <span className="as-person__text">
                    <span className="as-person__name">{s.name}</span>
                    <span className="as-person__meta">
                      {s.role === 'owner' ? 'Owner' : 'Staff'} · {staffLogin(s.last_login_at)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <p className="as-staff__foot">
            Click a person to see only the athletes they manage. Only the owner can invite or remove staff.
          </p>
        </aside>
      </div>

      {adding && <AddAthleteModal onClose={closeAdd} onCreated={created} />}
      {inviting && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="as-invite-title"
          onClick={closeInvite}>
          <div className="modal-panel as-invite-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title" id="as-invite-title">Invite staff</div>
            <StaffInvite onChange={load} />
            <button type="button" className="adm-btn" onClick={closeInvite}>Done</button>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
